import React, { useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { paths } from 'routes/paths';
import usePlanWizardMutations, {
  PlanWizardMode
} from './hooks/usePlanWizardMutations';
import {
  mapFetchedPlanToWizardValues,
  getCreateInitialValues
} from './helpers/planFormMapping';
import { planWizardValidationSchema } from './Constants/planFormikData';
import PlanStepsLayout from './PlanStepsLayout';
import { dismissPlanNotification } from './components/Notification';
import { CropDataProvider } from './PlanStepsLayout/CropDataContext';

interface DuplicateRouteState {
  flow?: 'duplicate';
  sourcePlanId?: string;
}

const TITLE_BY_MODE: Record<PlanWizardMode, string> = {
  create: 'Create Plan',
  edit: 'Edit Plan',
  duplicate: 'Create Plan'
};

/**
 * Route-level flow resolution + data fetch for the Plan wizard, rendered for
 * create/edit. Duplicate is route-state on the create route
 * (`location.state = { flow: 'duplicate', sourcePlanId }`), not its own route.
 */
const PlanRouteAdmin: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation<DuplicateRouteState | undefined>();
  const history = useHistory();
  useEffect(() => {
    dismissPlanNotification();
  }, []);

  const isDuplicate = location.state?.flow === 'duplicate';
  let mode: PlanWizardMode = 'create';
  if (id) {
    mode = 'edit';
  } else if (isDuplicate) {
    mode = 'duplicate';
  }
  const planId = mode === 'edit' ? id : location.state?.sourcePlanId;

  const { plan, loading, saving, submitPlan } = usePlanWizardMutations(
    mode,
    planId
  );

  // Memoized on [mode, plan] so re-renders (e.g. `saving` toggling) don't
  // redo the per-acre/total derivation and remapping unnecessarily.
  const initialValues = useMemo(
    () =>
      mode === 'create'
        ? getCreateInitialValues()
        : mapFetchedPlanToWizardValues(plan, {
            blankName: mode === 'duplicate',
            preserveAssociationIds: mode === 'edit'
          }),
    [mode, plan]
  );

  // `usePlanData('plan')` (create mode) still starts `loading: true` for one
  // tick before its own effect resolves it — only edit/duplicate have a real
  // fetch worth blocking on (mirrors `PlanDetailPanel.tsx`'s identical guard).
  const isFetchingSourcePlan = mode !== 'create' && loading;

  // `usePlanData` clears `loading` in a `finally` even when its fetch fails,
  // leaving `plan` at its default `{}` — a real fetched Plan always has an
  // `id`, so its absence once loading has settled means the fetch failed
  // (same heuristic `PlanDetailPanel.tsx`'s `isEmpty` check uses).
  const hasSourceLoadError = mode !== 'create' && !loading && !plan?.id;

  const goToPlansList = () => history.push(paths.plans);

  // The wizard shell always mounts immediately; `loadingSource` shows an
  // inline placeholder instead of blocking on a full-screen spinner.
  // `enableReinitialize` is required since `<Formik>` now mounts before the
  // source Plan fetch resolves.
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={planWizardValidationSchema}
      validateOnMount
      validateOnChange
      validateOnBlur
      onSubmit={() => {}}
    >
      <CropDataProvider>
        <PlanStepsLayout
          title={TITLE_BY_MODE[mode]}
          onSubmit={submitPlan}
          saving={saving}
          onSuccess={goToPlansList}
          onCancel={goToPlansList}
          loadingSource={isFetchingSourcePlan}
          loadError={hasSourceLoadError}
          isEditMode={mode === 'edit'}
        />
      </CropDataProvider>
    </Formik>
  );
};

export default PlanRouteAdmin;
